import { useState } from 'react';
import { Loader2, Square, Trash2 } from 'lucide-react';
import CollapsibleTile from './CollapsibleTile';
import { Button, Modal } from '../ui';

type LessonSessionManagementProps = {
  draftObservationCount: number;
  onEndSession: () => Promise<void>;
  onDestroySession: () => Promise<void>;
  ending?: boolean;
  destroying?: boolean;
};

export default function LessonSessionManagement({
  draftObservationCount,
  onEndSession,
  onDestroySession,
  ending = false,
  destroying = false
}: LessonSessionManagementProps) {
  const [destroyConfirmOpen, setDestroyConfirmOpen] = useState(false);

  const handleConfirmDestroy = async () => {
    try {
      await onDestroySession();
      setDestroyConfirmOpen(false);
    } catch {
      // Parent shows toast; keep dialog open for retry.
    }
  };

  return (
    <>
      <CollapsibleTile title="Stundenverwaltung" defaultOpen contentClassName="space-y-3">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Offene Beobachtungen: {draftObservationCount}
        </div>
        <Button
          variant="primary"
          size="md"
          className="w-full"
          disabled={ending || destroying}
          onClick={() => void onEndSession()}
        >
          {ending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
          Session beenden
        </Button>
        <Button
          variant="danger"
          size="md"
          className="w-full"
          disabled={ending || destroying}
          onClick={() => setDestroyConfirmOpen(true)}
        >
          {destroying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Session vernichten
        </Button>
      </CollapsibleTile>

      <Modal
        isOpen={destroyConfirmOpen}
        onClose={() => {
          if (!destroying) setDestroyConfirmOpen(false);
        }}
        title="Session vernichten?"
        size="sm"
        closeOnBackdrop={!destroying}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={destroying} onClick={() => setDestroyConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" disabled={destroying} onClick={() => void handleConfirmDestroy()}>
              {destroying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Endgültig vernichten
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Die gesamte Sitzung wird unwiderruflich gelöscht. Dazu gehören alle Belege und Kompetenzen aus dieser
          Session, Beobachtungen, Hilfe-Status und die gespeicherte Live-Session.
        </p>
      </Modal>
    </>
  );
}
